import { Directive, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthorizationService } from './authorization.service';
import { Permission } from './permission';

/**
 * Deaktiviert ein <fieldset> (und damit alle nativen Formularelemente darin),
 * solange der Benutzer die Berechtigung NICHT hat — "sichtbar, aber gesperrt".
 *
 * Bewusst NUR für <fieldset> erlaubt: einzelne Controls haben bereits eigene
 * [disabled]-Bindings (in-flight Spinner etc.); zwei Schreiber auf derselben
 * Property würden sich gegenseitig überschreiben. Ein disabled-Fieldset
 * gewinnt nativ immer, egal was das innere Control selbst bindet.
 *
 * Nicht erfasst (per HTML-Spezifikation): <a>-Links, Elemente mit (click)
 * auf Nicht-Formular-Tags (cds-icon, div[role=button]) — diese brauchen
 * eigenes Gating (*hasPermission bzw. [disabled]-Input).
 *
 * Zusammen mit der globalen Utility-Klasse .rbac-fieldset (display: contents)
 * ist der Wrapper layout-neutral.
 */
@Directive({
  selector: 'fieldset[disableUnlessPermission]',
  standalone: true,
})
export class DisableUnlessPermissionDirective implements OnDestroy {
  private permission?: Permission;
  private readonly sub: Subscription;

  constructor(
    private auth: AuthorizationService,
    private el: ElementRef<HTMLFieldSetElement>,
    private renderer: Renderer2,
  ) {
    this.sub = this.auth.roles$.subscribe(() => this.apply());
  }

  @Input({ required: true })
  set disableUnlessPermission(permission: Permission) {
    this.permission = permission;
    this.apply();
  }

  private apply(): void {
    if (!this.permission) return;
    const disabled = !this.auth.hasPermission(this.permission);
    this.renderer.setProperty(this.el.nativeElement, 'disabled', disabled);
    // Visuelles Feedback für Fälle, die der Browser nicht selbst dimmt.
    if (disabled) {
      this.renderer.addClass(this.el.nativeElement, 'rbac-disabled');
    } else {
      this.renderer.removeClass(this.el.nativeElement, 'rbac-disabled');
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
